import React, {
  ReactEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Image, Stack } from "react-bootstrap";
import { Game } from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./GameFeed.scss";
import ImageShimmer from "../../../images/shimmer.svg";
import ImageFallbackGamePoster from "../../../images/fallback-game-poster.png";
import ImageFallbackGameThumbnail from "../../../images/fallback-thumbnail.png";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import GenreTag from "../GenreTag/GenreTag";
import TournamentTile from "../TournamentTile/TournamentTile";
import UserAction from "../UserAction/UserAction";
import PlayIcon from "../../icons/PlayIcon";
import AddPersonIcon from "../../icons/AddPersonIcon";
import LoveSolidIcon from "../../icons/LoveSolidIcon";
import ShareIcon from "../../icons/ShareIcon";
import SpeakerOffIcon from "../../icons/SpeakerOffIcon";
import SpeakerOnIcon from "../../icons/SpeakerOnIcon";
import { formatPlayCount } from "../../../utils/helpers";
import {
  FunctionWithOptionalParams,
  FunctionWithParams,
  FunctionWithVoidReturn,
} from "../../../utils/constants/generic-types";
import {
  useGameMediaSelection,
  useVideoSeek,
} from "../../../hooks/useGamesPage";
import useInfiniteActiveTournamentsQuery from "../../../react-query/queries/use-infinite-active-tournaments";
import { GameScrollGuideMessages } from "../GameScrollGuideIndicator/GameScrollGuideIndicator";

interface GameFeedProps {
  game: Game;
  visibleVideo: HTMLVideoElement | null;
  isVisibleGame: boolean;
  videoIsMuted: boolean;
  gameIsFavorited?: boolean;
  isCreateShareLinkPending: boolean;
  handlePlayGame: FunctionWithVoidReturn;
  handleGenreClick: FunctionWithParams<void, string>;
  handlePlayTournament: FunctionWithVoidReturn;
  handleFavoriteGame: FunctionWithParams<void, Game>;
  handlePlayWithFriends: FunctionWithVoidReturn;
  handleShare: FunctionWithVoidReturn;
  toggleMute: FunctionWithOptionalParams<void, boolean>;
  setVideoIsLoading: FunctionWithParams<void, boolean>;
  isVideoPlaying: boolean;
  setVideoIsPlaying?: FunctionWithParams<void, boolean>;
  handleShowScrollIndicator?: FunctionWithOptionalParams<
    void,
    GameScrollGuideMessages
  >;
}

const GameFeed = forwardRef<HTMLVideoElement | null, GameFeedProps>(
  (props, ref) => {
    const {
      game,
      visibleVideo,
      isVisibleGame,
      videoIsMuted,
      gameIsFavorited,
      isCreateShareLinkPending,
      handlePlayGame,
      handleGenreClick,
      handlePlayTournament,
      handleFavoriteGame,
      handlePlayWithFriends,
      handleShare,
      toggleMute,
      setVideoIsLoading,
      isVideoPlaying,
      setVideoIsPlaying,
      handleShowScrollIndicator,
    } = props;

    const { activeTournaments } = useInfiniteActiveTournamentsQuery({
      gameId: game.id,
    });
    const handleVideoSeek = useVideoSeek(visibleVideo);
    const selectedMediaIndex = useGameMediaSelection({
      gameId: game.id,
      gameMedia: game.media,
    });
    const selectedMediaUrl = useMemo(
      () =>
        // The check, game.media && selectedMediaIndex !== undefined, is intentional since selectedMediaIndex could be zero.
        // If we did just "game.media && selectedMediaIndex", it means we would never show the first game media in the list.
        game.media && selectedMediaIndex !== undefined
          ? game.media[selectedMediaIndex].fileKey
          : "",
      [game.media, selectedMediaIndex]
    );
    const [videoCurrentTime, setVideoCurrentTime] = useState<number>(0);
    const [videoDuration, setVideoDuration] = useState<number | undefined>(
      visibleVideo?.duration
    );
    const { t } = useTranslation("games");
    const animationFrameRef = useRef<number>();

    const handleVideoTimeUpdate: ReactEventHandler<HTMLVideoElement> =
      useCallback(
        (event) => {
          const { duration, currentTime } = event.currentTarget;
          const videoIsAboutToEnd = currentTime >= 0.85 * duration; // up to 85% of the video has played

          if (videoIsAboutToEnd) handleShowScrollIndicator?.();
          if (!videoDuration) setVideoDuration(duration);
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
          animationFrameRef.current = requestAnimationFrame(() => {
            const timeDiff = Math.abs(currentTime - videoCurrentTime);
            if (timeDiff > 0.5) setVideoCurrentTime(currentTime);
          });
        },
        [videoDuration, videoCurrentTime, handleShowScrollIndicator]
      );

    const handlePlayPause: ReactEventHandler<HTMLVideoElement> =
      useCallback(() => {
        // There's a quirky behaviour where sometimes the video's pause status would be true even when it's playing.
        // This happens when the user scrolls to the next game without any prior play/pause action
        // Hence, we want to ensure that the video's pause status is aligned with our in-memory state.
        if (visibleVideo?.paused && !isVideoPlaying) {
          setVideoIsPlaying?.(true);
          visibleVideo?.play().catch(() => setVideoIsPlaying?.(false));
        } else {
          setVideoIsPlaying?.(false);
          visibleVideo?.pause();
        }
      }, [visibleVideo, isVideoPlaying, setVideoIsPlaying]);

    // This would ensure that the loading shimmer doesn't show endlessly for games that don't have any media
    useEffect(() => {
      if (!game.media?.length) setVideoIsLoading(false);
    }, [game.media?.length, setVideoIsLoading]);

    return (
      <div key={game.id} className="GameFeed">
        <div className="GameVideoContainer">
          <Image
            src={game.newPoster || game.poster || ImageFallbackGamePoster}
            alt={game.name}
            className="GamePoster"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = ImageFallbackGamePoster;
            }}
          />
          {isVisibleGame && selectedMediaUrl && (
            <video
              loop
              playsInline
              preload="auto"
              src={selectedMediaUrl}
              ref={ref}
              className="GameVideo"
              onClick={handlePlayPause}
              onTimeUpdate={handleVideoTimeUpdate}
              onLoadedData={() => setVideoIsLoading(false)}
            >
              {t("sorry_your_browser_does_not_support_video_content")}
            </video>
          )}
          <button className="VideoSeekButton" onPointerDown={handleVideoSeek}>
            <progress
              max={videoDuration || 1}
              value={videoCurrentTime}
              className="VideoProgress"
            >
              {videoCurrentTime}
            </progress>
          </button>
        </div>

        <div className="GameplayFooter">
          <div className="GameplayDetails">
            <Stack
              gap={2}
              className="justify-content-end GameAndTournamentDetails"
            >
              <Stack
                direction="horizontal"
                gap={2}
                className="align-items-center allow-pointer-events"
              >
                <button
                  type="button"
                  onClick={handlePlayGame}
                  className="p-0 GameThumbnailBtn"
                >
                  <Image
                    src={game.thumbnail || ImageFallbackGameThumbnail}
                    alt={`${game.name} thumbnail`}
                    width={53}
                    height={53}
                    className="GameThumbnail"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = ImageFallbackGameThumbnail;
                    }}
                  />
                </button>
                <Stack gap={1} className="align-self-center">
                  {!!game.categories?.length && (
                    <Stack direction="horizontal" gap={1}>
                      {game.categories.map((genre) => (
                        <GenreTag
                          key={genre.id}
                          genre={genre.name ?? ""}
                          icon={genre.icon ?? ""}
                          handleClick={() => handleGenreClick(genre.name ?? "")}
                        />
                      ))}
                    </Stack>
                  )}
                  <p className="text-capitalize Hanson-700 SpecialFontSettings GameName">
                    {game.name}
                  </p>
                  {/* We'll show this again when we start getting the game replay user info from the backend */}
                  {/* <div className="Grotesk SpecialFontSettings GamerInfo">
                    <span>Watching game replay of</span>
                    <Image
                      src={ImageAvatar8}
                      alt="User @evaniroc"
                      width={14}
                      height={14}
                    />
                    <span>@evaniroc</span>
                  </div> */}
                </Stack>
              </Stack>
              {activeTournaments && activeTournaments.length > 0 && (
                <div className="allow-pointer-events">
                  <TournamentTile
                    tournaments={activeTournaments}
                    handlePlayTournament={handlePlayTournament}
                  />
                </div>
              )}
            </Stack>
            <div className="allow-pointer-events UserActions">
              <UserAction
                icon={<PlayIcon />}
                text={formatPlayCount(game.playCount ?? 0)}
                handleUserAction={handlePlayGame}
              />
              <UserAction
                icon={<AddPersonIcon />}
                text={t("common:play_with_friends")}
                handleUserAction={handlePlayWithFriends}
              />
              <UserAction
                icon={
                  <LoveSolidIcon fill={gameIsFavorited ? "#FC4E5D" : "white"} />
                }
                text={game.favoriteCount}
                handleUserAction={() => handleFavoriteGame(game)}
              />
              <UserAction
                icon={
                  <ShareIcon
                    fill={isCreateShareLinkPending ? "#888888" : "white"}
                    stroke={isCreateShareLinkPending ? "#888888" : "white"}
                  />
                }
                text={t("share")}
                handleUserAction={handleShare}
                textStyle={{
                  color: isCreateShareLinkPending ? "#888888" : "white",
                }}
              />
              <UserAction
                icon={videoIsMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
                handleUserAction={() => toggleMute()}
              />
            </div>
          </div>
          <div className="pe-1 allow-pointer-events PlayGameBtnWrap">
            <PrimaryActionButton
              size="lg"
              text={t("play_game")}
              leftImage={<PlayIcon />}
              onClick={handlePlayGame}
              buttonClassName="PlayGameBtn"
            />
            <span className="PlayGameBtnShimmer" aria-hidden>
              <Image src={ImageShimmer} alt="" className="shimmer" />
            </span>
          </div>
        </div>
      </div>
    );
  }
);

export default GameFeed;
